'use client';

import React from "react";
import PointCard from "@/components/permuta/PointCard";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import Image from "next/image";
import {ChevronLeft, ChevronRight} from "lucide-react";

export interface Location {
    _id: string;
    title: string;
    address: string;
    email: string;
    phone: string;
    hours: string;
    imgUrl: string;
    longitude: number;
    latitude: number;
    linkMaps: string;
    slug: string;
}

export default function DoveSiamoSlide({locations}: { locations: Location[] }) {
    const [carouselApi, setCarouselApi] = React.useState<any>();
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    React.useEffect(() => {
        if (!carouselApi) {
            return;
        }

        const updateScrollButtons = () => {
            setCanScrollPrev(carouselApi.canScrollPrev());
            setCanScrollNext(carouselApi.canScrollNext());
        };

        // Update buttons on mount
        updateScrollButtons();

        // Update buttons on scroll
        carouselApi.on("scroll", updateScrollButtons);
        carouselApi.on("reInit", updateScrollButtons);

        return () => {
            carouselApi.off("scroll", updateScrollButtons);
            carouselApi.off("reInit", updateScrollButtons);
        };
    }, [carouselApi]);

    // Gestione del caso in cui non ci sono location
    if (!locations || locations.length === 0) {
        return (
            <div className="w-full max-w-7xl mx-auto px-4 py-10">
                <h2 className="text-3xl font-normal mb-4">Dove siamo</h2>
                <p className="text-gray-500">Nessuna location disponibile al momento.</p>
            </div>
        );
    }

    return (
        <section className="w-full py-8 md:py-12 relative">
            <div className="flex items-center justify-center w-full gap-x-3">
                <Image src="/dove-siamo-icon.png" alt="Logo Consorzio" width={80} height={80}
                       className="w-[40px] h-[40px] max-sm:w-[34px] max-sm:h-[34px] mb-4"/>
                <h2 className="font-normal mb-4 text-4xl max-sm:text-2xl">Le nostre sedi</h2>
            </div>

            <div className="relative ">
                {/* Navigation arrows positioned at top right */}
                <div className="container flex justify-end gap-x-3 z-10">
                    <button
                        onClick={() => carouselApi?.scrollPrev()}
                        disabled={!canScrollPrev}
                        className={`h-10 w-10 border-2 flex items-center justify-center rounded-full shadow-sm transition-all duration-200 ${
                            canScrollPrev
                                ? 'border-primary bg-white hover:bg-gray-50 text-primary cursor-pointer'
                                : 'border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed'
                        }`}
                    >
                        <ChevronLeft />
                    </button>

                    <button
                        onClick={() => carouselApi?.scrollNext()}
                        disabled={!canScrollNext}
                        className={`h-10 w-10 border-2 flex items-center justify-center rounded-full shadow-sm transition-all duration-200 ${
                            canScrollNext
                                ? 'border-primary bg-white hover:bg-gray-50 text-primary cursor-pointer'
                                : 'border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed'
                        }`}
                    >
                        <ChevronRight />
                    </button>
                </div>

                <Carousel
                    className="w-full   mx-auto"
                    opts={{
                        align: "start",
                        loop: false,
                        dragFree: true,
                        containScroll: "trimSnaps"
                    }}
                    setApi={setCarouselApi}
                >
                    <CarouselContent className="ml-1 md:ml-12 lg:ml-14">
                        {locations.map((location: Location) => (
                            <CarouselItem
                                key={location._id}
                                className="pr-3 md:pr-4 basis-[280px] sm:basis-[320px] md:basis-[350px] lg:basis-[380px]"
                            >
                                <div className="py-4 h-full flex">
                                    <PointCard locations={location}/>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </div>
        </section>
    );
}
